import { mapState } from 'pinia';
import stores from 'store/modules';
import VoiceOrVideoMixin from 'mixins/voice-or-video';
import UserMixin from 'mixins/user';
import Canny from 'utils/canny';
import { setOrganizationData, setUserData } from 'services/segment';
import { updateIntercom } from 'core/Intercom';
import FeatureablesMixin from 'mixins/featureables';

export default {
    data: () => ({
        cannyIdentified: false,
        autorechargeFailure: false,
    }),

    mixins: [VoiceOrVideoMixin, UserMixin, FeatureablesMixin],

    computed: {
        isWidget() {
            return Boolean(this.$route.name?.includes('widget'));
        },

        ...mapState(stores.User, ['user', 'userPermissions', 'loadingApp', 'config']),
        ...mapState(stores.BillingSources, [
            'billingPlans',
            'billingSources',
            'billingSourcesSimple',
        ]),
        ...mapState(stores.Number, ['numbers']),
        ...mapState(stores.Organization, ['organization']),
        ...mapState(stores.Subscriptions, ['subscription']),
        ...mapState(stores.Inboxes, ['inboxesArray']),
    },

    watch: {
        '$route.name'() {
            updateIntercom();
        },
    },

    methods: {
        initVue() {
            this.initUser();
        },

        async initUser() {
            if (this.user?.id) {
                document.body.classList.remove('is-auth');

                this.$store.dispatch('getAllShortenerDomains');

                if (this.isWidget) {
                    await Promise.all([this.$store.dispatch('getIntegrations')]);

                    this.$store.dispatch('updateStateDlcProfile', ['limitsLoading', false]);
                    this.$store.dispatch('updateStateBillingSources', ['plansLoading', false]);
                    this.$store.dispatch('loadingBillingSource', false);
                    this.$store.dispatch('setLoadingMain', false);
                }

                if (!this.isWidget) {
                    await Promise.all([
                        this.handleFetchNumbers(),
                        this.handleCheckDlcLimits(),
                        this.handleLoadBillingSources(),
                        this.handleFetchTollFreeProfile(),
                        this.handledGetBillingPlan(),
                        this.handleGetSignatureMessage(),
                        this.$store.dispatch('getIntegrations'),
                    ]);

                    this.$store.dispatch('setLoadingMain', false);
                }

                this.initRetently();
                if (!this.hasFeatureUser('salesmsg-design-v2')) {
                    await this.initCoupon();
                }
            } else {
                document.body.classList.add('is-auth');
                this.$store.dispatch('updateStateDlcProfile', ['limitsLoading', false]);
                this.$store.dispatch('updateStateBillingSources', ['plansLoading', false]);
                this.$store.dispatch('loadingBillingSource', false);
                this.$store.dispatch('setLoadingMain', false);
            }

            if (this.organization && this.user) {
                this.cannyIdentify();
            }
        },

        async handleFetchNumbers() {
            try {
                if (!this.numbers.length) {
                    await this.$store.dispatch('getNumbers', { search: true });
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleFetchNumbers');
            }
        },

        async handleCheckDlcLimits() {
            if (!this.numbers.length && this.organization.hasTwilioNumbers) {
                await this.$store.dispatch('getOrganization10DlcLimits');
            }

            this.$store.dispatch('updateStateDlcProfile', ['limitsLoading', false]);
        },

        async handleLoadBillingSources() {
            try {
                if (
                    this.user &&
                    this.userPermissions.can_manage_billing_settings &&
                    !this.billingSources.length &&
                    this.subscription
                ) {
                    await this.$store.dispatch('getBillingSources');
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleLoadBillingSources');
            } finally {
                this.$store.dispatch('loadingBillingSource', false);
            }
        },

        async handleFetchTollFreeProfile() {
            try {
                if (this.userPermissions.can_manage_organization_settings) {
                    await this.$store.dispatch('fetchTollFreeProfile');
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleFetchTollFreeProfile');
            }
        },

        async handledGetBillingPlan() {
            try {
                if (!this.billingPlans) {
                    await this.$store.dispatch('getBillingPlansMicroservice');
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handledGetBillingPlan');
            }
        },

        async initSetupTwilioCheckMedia() {
            if (!this.inboxesArray.length) return;
            if (this.isWidget) return;
            if (this.isIframe() && this.isHidden()) return;

            this.setupTwilioCheckMedia();
        },

        isIframe() {
            try {
                return window.self !== window.top;
            } catch (e) {
                return true;
            }
        },

        isHidden() {
            const { width, height } = window.document.body.getBoundingClientRect();

            return width === 0 && height === 0;
        },

        async handleGetSignatureMessage() {
            try {
                await this.$store.dispatch('getSignatureMessage');
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'handleGetSignatureMessage');
            }
        },

        async cannyIdentify() {
            try {
                if (
                    this.user &&
                    this.user.id &&
                    this.organization &&
                    this.organization.id &&
                    this.subscription &&
                    this.hasIntegrations
                ) {
                    await this.$store.dispatch('getOrganizationMembers');

                    const integrations = this.getIntegrationskeys;

                    if (window.Canny) {
                        Canny.init({
                            integrations,
                            user: this.user,
                            organization: this.organization,
                            subscription: this.subscription,
                            members: this.members,
                            cannyFlag: this.cannyIdentified,
                        });

                        setUserData();
                        setOrganizationData();
                    }
                }
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'cannyIdentify');
            }
        },

        async initCoupon() {
            await this.$store.dispatch('getUserMe');
            const currentUrl = window.location.href;
            const queryString = currentUrl.split('?')[1];
            const searchParams = new URLSearchParams(queryString);

            const c = searchParams.get('c');

            if (c) {
                try {
                    const couponData = await this.$store.dispatch('applyCoupon', c);
                    if (couponData.error) {
                        await this.$store.dispatch('showToast', {
                            type: 'danger',
                            message: couponData.error,
                        });
                    } else {
                        await this.$store.dispatch('showToast', {
                            type: 'success',
                            message: 'Coupon successfully applied!',
                        });
                    }
                } catch (e) {
                    await this.$store.dispatch('showToast', {
                        type: 'danger',
                        message: 'Error during coupon processing',
                    });
                }
            }
        },

    },
};
